import React, {useEffect, useState} from 'react';
import Container from "../../components/UI/Container/Container";
import Block from "../../components/UI/Block/Block";
import {
  Alert,
  Avatar,
  Button, Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Radio,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Typography,
  Upload
} from "antd";
import {
  CheckOutlined,
  FileTextOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SolutionOutlined,
  UploadOutlined, UsergroupAddOutlined,
  UserOutlined
} from "@ant-design/icons";
import {FIELDS_SCHEME, GENERAL_SCHEME} from "../../schemas";
import classes from './CreateCorporation.module.scss';
import {API} from "../../http";
import {NEW_ENDPOINTS} from "../../http/endpoints";
import {ROUTES} from "../../components/AppRouter/AppRouter";
import {useNavigate} from "react-router-dom";
import {handleFileToBase64} from "../../utils/helpers";
import {countries} from "../../utils/countries";
import AddCompanyMemberModal from "../../components/AddCompanyMemberModal/AddCompanyMemberModal";
import {generateSumSubLink} from "../../actions/users";
import _ from "lodash";


const CreateCorporation = () => {
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [corporationId, setCorporationId] = useState();
  const [members, setMembers] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [form] = Form.useForm();
  const [filesForm] = Form.useForm();
  const navigate = useNavigate();

  const next = () => setCurrent(current + 1);
  const prev = () => setCurrent(current - 1);

  const handleBeforeUnload = (event) => {
    if (form.isFieldsTouched()) {
      // localStorage.setItem('formData', JSON.stringify(form.getFieldsValue()));
      event.preventDefault();
      event.returnValue = 'You have unsaved changes. Do you really want to leave?';
    }
  };

  useEffect(() => {
    // const savedData = localStorage.getItem('formData');
    // if (savedData) {
    //   form.setFieldsValue(JSON.parse(savedData));
    // }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleAddMember = async (member) => {
    const { position, percent, user_id: formUserId, ...formData } = member;
    setLoading(true);
    try {
      if (formUserId) {
        await API.post(NEW_ENDPOINTS.ADMIN.USER.CORPORATION.ADD_MEMBER, {
          corporate_id: corporationId,
          member_id: formUserId,
          position,
          percent,
        });
        setMembers([...members, {...member, id: formUserId}]);
      } else {
        const {data: { result: { success, data: msg, user_id } }} = await API.post(NEW_ENDPOINTS.ADMIN.USER.CREATE, {
          ...formData,
          email: formData.email_address,
        });
        if (success) {
          await API.post(NEW_ENDPOINTS.ADMIN.USER.CORPORATION.ADD_MEMBER, {
            corporate_id: corporationId,
            member_id: user_id,
            position,
            percent,
          });
          setMembers([...members, {...member, id: user_id}]);
        } else {
          message.error(msg);
        }
      }
    } catch (e) {
      message.error('Error adding member');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (formData) => {
    let ownerData = [];
    if (formData.owners && formData.owners.length > 0) {
      const owner = formData.owners[0];
      ownerData = [
        {
          first_name: owner.owner_first_name,
          last_name: owner.owner_surname,
          email: owner.owner_email_address,
          position: 'owner',
          percent: owner.owner_ownership_percentage,
        }
      ];
    }
    setLoading(true);
    try {
      const membersPayload = [
        {
          first_name: formData.director_first_name,
          last_name: formData.director_surname,
          email: formData.director_email_address,
          position: 'director',
        },
        ...ownerData,
      ];
      const { data: { result: { success, data: msg, user_id } } } = await API.post(NEW_ENDPOINTS.ADMIN.USER.CORPORATION.CREATE, {
        first_name: formData.first_name,
        last_name: "",
        email: formData.email,
        members: membersPayload,
        info: [
          {
            name: "Company Name",
            value: formData.first_name,
            num: 1,
            section: 'Company information'
          },
          {
            name: 'Registration Number',
            value: formData.registration_number,
            num: 2,
            section: 'Company information'
          },
          {
            name: 'TIN Number',
            value: formData.tin_number,
            num: 3,
            section: 'Company information'
          },
          {
            name: 'Date of Registration',
            value: formData.date_of_registration.format("MMMM D, YYYY"),
            num: 4,
            section: 'Company information'
          },
          {
            name: 'Registration Country',
            value: formData.registration_country,
            num: 5,
            section: 'Company information'
          },
          {
            name: 'SIC/NACE Code',
            value: formData.sic_code,
            num: 6,
            section: 'Company information'
          },
          {
            name: 'Registered Capital',
            value: formData.registered_capital,
            num: 7,
            section: 'Company information'
          },
          {
            name: 'Registered Address',
            value: formData.registered_address,
            num: 8,
            section: 'Company information'
          },
          {
            name: 'Correspondence Address',
            value: formData.correspondence_address,
            num: 9,
            section: 'Company information'
          },
          {
            name: 'Website',
            value: formData.website ?? "",
            num: 10,
            section: 'Company information'
          },
          {
            name: 'Phone Number',
            value: formData.phone_number,
            num: 11,
            section: 'Company information'
          },
          {
            name: 'Email',
            value: formData.email,
            num: 12,
            section: 'Company information'
          },
          {
            name: 'License Number',
            value: formData.license_number || '-/-',
            num: 13,
            section: 'Company information'
          },
          {
            name: 'Authority',
            value: formData.authority || '-/-',
            num: 14,
            section: 'Company information'
          },{
            name: "Does the company make payments in the interest of third parties?",
            value: formData.make_payments ? "Yes" : "No",
            num: 1,
            section: 'Operational information'
          },
          {
            name: "Is the Company a regulated market entity?",
            value: formData.regulated_entity ? "Yes" : "No",
            num: 2,
            section: 'Operational information'
          },
          formData.regulated_entity && {
            name: "Full Name of the Authority and Web Address",
            value: formData.regulatory_authority,
            num: 3,
            section: 'Operational information'
          },
          {
            name: "Business Activity",
            value: formData.business_activity,
            num: 4,
            section: 'Operational information'
          },
          {
            name: "Purpose of the Account",
            value: formData.purpose_account,
            num: 5,
            section: 'Operational information'
          },
          {
            name: "Services to be Used",
            value: formData.services_used,
            num: 6,
            section: 'Operational information'
          },
          {
            name: "Expected Volume of Incoming Transactions",
            value: formData.incoming_transactions,
            num: 7,
            section: 'Operational information'
          },
          {
            name: "Number of Incoming Transactions",
            value: formData.number_incoming_transactions,
            num: 8,
            section: 'Operational information'
          },
          {
            name: "Expected Volume of Outgoing Transactions",
            value: formData.outgoing_transactions,
            num: 9,
            section: 'Operational information'
          },
          {
            name: "Number of Outgoing Transactions",
            value: formData.number_outgoing_transactions,
            num: 10,
            section: 'Operational information'
          },
          {
            name: "Has your company ever been fined or defined as a shell company?",
            value: formData.fined_or_shell ? "Yes" : "No",
            num: 11,
            section: 'Operational information'
          },
          formData.fined_or_shell && {
            name: "Provide details",
            value: formData.fined_or_shell_details,
            num: 12,
            section: 'Operational information'
          },
          {
            name: "Do you have accounts with other financial institutions?",
            value: formData.financial_ins_accounts ? "Yes" : "No",
            num: 13,
            section: 'Operational information'
          },
          formData.financial_ins_accounts && {
            name: "Provide details (account number and names of the institutions)",
            value: formData.financial_ins_accounts_details,
            num: 14,
            section: 'Operational information'
          },
          {
            name: "Director's First Name",
            value: formData.director_first_name,
            num: 1,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Director's Surname",
            value: formData.director_surname,
            num: 2,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Director's Date of Birth",
            value: formData.director_date_of_birth.format("MMMM D, YYYY"),
            num: 3,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Director's Nationality",
            value: formData.director_nationality,
            num: 4,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Director's Residence Address",
            value: formData.director_residence_address,
            num: 5,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Director's Phone Number",
            value: formData.director_phone_number,
            num: 6,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Director's Email Address",
            value: formData.director_email_address,
            num: 7,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Director's Preferred Channel for Communication",
            value: formData.director_preferred_communication_channel,
            num: 8,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Director Serves as an Authorized Signatory?",
            value: formData.director_authorized_signatory ? "Yes" : "No",
            num: 9,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Is Director a politically exposed person (PEP)?",
            value: formData.director_politically_exposed_person ? "Yes" : "No",
            num: 10,
            section: 'Director(s) Personal Details'
          },
          {
            name: "Is Director a U.S. person?",
            value: formData.director_us_person ? "Yes" : "No",
            num: 11,
            section: 'Director(s) Personal Details'
          },
          formData.director_us_person && {
            name: "Director's TIN Number",
            value: formData.director_us_person_tin,
            num: 12,
            section: 'Director(s) Personal Details'
          },
          ...(ownerData.length > 0 ? [
            {
              name: "Beneficial Owner's First Name",
              value: formData.owners[0].owner_first_name,
              num: 1,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Beneficial Owner's Surname",
              value: formData.owners[0].owner_surname,
              num: 2,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Beneficial Owner's Date of Birth",
              value: formData.owners[0].owner_date_of_birth.format("MMMM D, YYYY"),
              num: 3,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Beneficial Owner's Nationality",
              value: formData.owners[0].owner_nationality,
              num: 4,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Percentage of Ownership",
              value: formData.owners[0].owner_ownership_percentage,
              num: 5,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Beneficial Owner's Residence Address",
              value: formData.owners[0].owner_residence_address,
              num: 6,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Beneficial Owner's Phone Number",
              value: formData.owners[0].owner_phone_number,
              num: 7,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Beneficial Owner's Email Address",
              value: formData.owners[0].owner_email_address,
              num: 8,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Beneficial Owner's Preferred Channel for Communication",
              value: formData.owners[0].owner_preferred_communication,
              num: 9,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Is Beneficial Owner a politically exposed person (PEP)?",
              value: formData.owners[0].owner_politically_exposed ? "Yes" : "No",
              num: 10,
              section: 'Beneficial Owner(s) Personal Details'
            },
            {
              name: "Is Beneficial Owner a U.S. person?",
              value: formData.owners[0].owner_us_person ? "Yes" : "No",
              num: 11,
              section: 'Beneficial Owner(s) Personal Details'
            },
            formData.owners[0].owner_us_person && {
              name: "Beneficial Owner's TIN Number",
              value: formData.owners[0].owner_tin_number,
              num: 12,
              section: 'Beneficial Owner(s) Personal Details'
            },
          ] : []),
        ].filter(Boolean),
      });
      if (success) {
        setCorporationId(user_id);
        message.success('Corporation created successfully');
        localStorage.removeItem('formData');

        try {
          const { data: { members: corpMembers = [] } } = await API.post(NEW_ENDPOINTS.ADMIN.USER.CORPORATION.INFO, {
            corporate_id: user_id,
          });
          if (corpMembers.length > 0) {
            setMembers(corpMembers.map(i => ({
              id: i.user.id,
              first_name: i.account.first_name,
              last_name: i.account.last_name,
              email_address: i.account.email_address,
              position: i?.position,
              percent: i?.percent,
            })));
          }
        } catch (e) {
          message.error('Error fetching corporation info');
        }
      } else {
        message.error(msg);
      }
    } catch (e) {
      message.error('Something went wrong...');
      console.error('Error creation corporation: ', e);
    } finally {
      setLoading(false);
    }
  };
  const handleDownloadTemplate = async () => {
    setLoading(true);
    try {
      const { data } = await API.post(NEW_ENDPOINTS.ADMIN.USER.CORPORATION.GENERATE_DOC, {
        corporate_id: corporationId,
      }, {
        responseType: 'blob',
      });
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'doc_to_sign.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      message.error('Something went wrong...');
      console.error('Error downloading template: ', e);
    } finally {
      setLoading(false);
    }
  };

  const sendSignedDocument = async ({signed_document, need_confirm}) => {
    setLoading(true);
    try {
      const signedFileBase64 = await handleFileToBase64(signed_document);
      await API.post(NEW_ENDPOINTS.ADMIN.USER.CORPORATION.SIGN, {
        corporate_id: corporationId,
        pdf: signedFileBase64,
      });
      if (need_confirm) {
        try {
          await API.post(NEW_ENDPOINTS.ADMIN.USER.CORPORATION.CONFIRM, {
            corporate_id: corporationId,
            corporate_confirm: true,
          })
        } catch (e) {
          message.error('Error confirming corporation');
          console.error('Error confirming corporation: ', e);
        }
      }
      next();
    } catch (e) {
      message.error('Failed to upload signed document');
      console.error('Error uploading signed document: ', e);
    } finally {
      setLoading(false);
    }
  };
  const handleBeforeFileUpload = (file) => {
    if (file.type !== 'application/pdf') {
      message.error('Only PDF files are allowed');
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > 100) {
      message.error('File must be smaller than 100MB');
      return Upload.LIST_IGNORE;
    }
    return false;
  };
  const handleFileUploadChange = async (event, name) => {
    const files = event.target.files;
    if (files.length === 0) {
      message.error(`Please upload the ${name}`);
      return;
    }
    const file = files[0];
    const fileBase64 = await handleFileToBase64(file);
    try {
      await API.post(NEW_ENDPOINTS.ADMIN.USER.CORPORATION.ADD_INFO, {
        corporate_id: corporationId,
        name,
        value: fileBase64,
        is_file: true,
        section: 'Required Documents for Account Opening',
      });
      message.success(`${name} uploaded successfully`);
    } catch (e) {
      message.error(`Failed to upload ${name}`);
      filesForm.setFieldsValue({
        [event.target.id]: undefined,
      });
    }
  };

  const steps = [
    {
      title: 'General information',
      content: () => (
        <Space direction="vertical" className="w100">
          <Form disabled={corporationId} className="w100" size="large" variant="filled" form={form} layout="vertical" onFinish={handleConfirm}>
            <Typography.Title level={4}>Company information</Typography.Title>
            <Row gutter={[32, 0]}>
              <Col xs={24}>
                <Form.Item label="Company Name" name="first_name" rules={GENERAL_SCHEME.required}>
                  <Input placeholder="Company Name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Registration Number" name="registration_number" rules={GENERAL_SCHEME.required} required>
                  <Input placeholder="Registration Number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item rules={GENERAL_SCHEME.required} label="TIN Number" name="tin_number" required>
                  <Input placeholder="TIN Number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item rules={GENERAL_SCHEME.required} label="Date of Registration" name="date_of_registration" required>
                  <DatePicker disabledDate={(current) => current && current > new Date()} className="w100" format={{ format: "MMMM D, YYYY" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="registration_country"
                  label="Registration Country"
                  rules={GENERAL_SCHEME.required}
                  required
                >
                  <Select placeholder="Registration Country" options={Object.values(countries).map(country => ({
                    label: country.cn,
                    value: country.cc,
                  }))} optionFilterProp="children"
                          filterOption={(input, option) => {
                            return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }} showSearch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item rules={GENERAL_SCHEME.required} label="SIC/NACE Code" name="sic_code" required>
                  <Input placeholder="SIC/NACE Code" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item rules={GENERAL_SCHEME.required} label="Registered Capital" name="registered_capital" required>
                  <InputNumber className="w100" min={0} suffix="EUR" placeholder="Registered Capital" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item rules={GENERAL_SCHEME.required} label="Registered Address" name="registered_address" required>
                  <Input.TextArea placeholder="Registered Address" autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item rules={GENERAL_SCHEME.required} label="Correspondence Address" name="correspondence_address" required>
                  <Input.TextArea placeholder="Correspondence Address" autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Website (optional)" name="website">
                  <Input placeholder="Website URL" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item rules={[...GENERAL_SCHEME.required, ...GENERAL_SCHEME.numeric]} label="Phone Number" name="phone_number" required>
                  <Input placeholder="Phone Number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Email" name="email" rules={[...GENERAL_SCHEME.required, ...FIELDS_SCHEME.email]} required>
                  <Input placeholder="Email" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="License Number (if applicable)" name="license_number">
                  <Input placeholder="License Number" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Authority (if applicable)" name="authority">
                  <Input placeholder="Authority" />
                </Form.Item>
              </Col>
            </Row>
            <Divider/>
            <Typography.Title level={4}>Operational information</Typography.Title>
            <Row gutter={[32, 0]}>
              <Col span={24}>
                <Form.Item
                  name="make_payments"
                  label="Does the company make payments in the interest of third parties?"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="regulated_entity"
                  label="Is the Company a regulated market entity?"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  shouldUpdate={(prevValues, curValues) => prevValues.regulated_entity !== curValues.regulated_entity}
                  noStyle
                >
                  {({ getFieldValue }) =>
                    getFieldValue('regulated_entity') === true ? (
                      <Form.Item
                        name="regulatory_authority"
                        label="Full Name of the Authority and Web Address"
                        rules={[...GENERAL_SCHEME.required]}
                      >
                        <Input placeholder="Authority Name and Web URL" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="business_activity"
                  label="Business Activity (including types of products/services provided)"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <Input.TextArea placeholder="Describe business activity" autoSize={{ minRows: 3, maxRows: 5 }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="purpose_account"
                  label="Purpose of the Account"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <Input placeholder="Purpose of the Account" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="services_used"
                  label="Services to be Used"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <Input placeholder="Services to be used by the company" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="incoming_transactions"
                  label="Expected Volume of Incoming Transactions"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <InputNumber className="w100" placeholder="Amount" min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="number_incoming_transactions"
                  label="Number of Incoming Transactions"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <InputNumber className="w100" placeholder="Number" min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="outgoing_transactions"
                  label="Expected Volume of Outgoing Transactions"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <InputNumber className="w100" placeholder="Amount" min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="number_outgoing_transactions"
                  label="Number of Outgoing Transactions"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <InputNumber className="w100" placeholder="Number" min={0} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="fined_or_shell"
                  label="Has your company ever been fined or defined as a shell company?"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  shouldUpdate={(prevValues, curValues) => prevValues.fined_or_shell !== curValues.fined_or_shell}
                  noStyle
                >
                  {({ getFieldValue }) =>
                    getFieldValue('fined_or_shell') === true ? (
                      <Form.Item
                        name="fined_or_shell_details"
                        label="Provide details"
                        rules={[...GENERAL_SCHEME.required]}
                      >
                        <Input.TextArea placeholder="Provide details" autoSize={{ minRows: 3, maxRows: 5 }} />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="financial_ins_accounts"
                  label="Do you have accounts with other financial institutions?"
                  rules={[...GENERAL_SCHEME.required]}
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  shouldUpdate={(prevValues, curValues) => prevValues.financial_ins_accounts !== curValues.financial_ins_accounts}
                  noStyle
                >
                  {({ getFieldValue }) =>
                    getFieldValue('financial_ins_accounts') === true ? (
                      <Form.Item
                        name="financial_ins_accounts_details"
                        label="Provide details (account number and names of the institutions)"
                        rules={[...GENERAL_SCHEME.required]}
                      >
                        <Input.TextArea placeholder="Provide details (account number and names of the institutions)" autoSize={{ minRows: 3, maxRows: 5 }} />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
            <Divider/>
            <Typography.Title level={4}>Director(s) Personal Details</Typography.Title>
            <Row gutter={[32, 0]}>
              <Col span={12}>
                <Form.Item
                  name="director_first_name"
                  label="Name"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Input placeholder="First Name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="director_surname"
                  label="Surname"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Input placeholder="Surname" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="director_date_of_birth"
                  label="Date of Birth"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <DatePicker disabledDate={(current) => current && current > new Date()} className="w100" format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="director_nationality"
                  label="Nationality"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Select placeholder="Nationality" options={Object.values(countries).map(country => ({
                    label: country.cn,
                    value: country.cc,
                  }))} optionFilterProp="children"
                          filterOption={(input, option) => {
                            return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }} showSearch />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="director_residence_address"
                  label="Residence Address"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Input.TextArea placeholder="Residence Address" autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="director_phone_number"
                  label="Phone Number"
                  rules={[...GENERAL_SCHEME.required, ...GENERAL_SCHEME.numeric]}
                  required
                >
                  <Input placeholder="Phone Number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="director_email_address"
                  label="Email Address"
                  rules={[...GENERAL_SCHEME.required, ...FIELDS_SCHEME.email]}
                  required
                >
                  <Input placeholder="Email Address" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="director_preferred_communication_channel"
                  label="Preferred Channel for Communication"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Radio.Group>
                    <Radio value="email">Email</Radio>
                    <Radio value="phone">Phone</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="director_authorized_signatory"
                  label="Director Serves as an Authorized Signatory?"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="director_politically_exposed_person"
                  label="Is Director a politically exposed person (PEP)?"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="director_us_person"
                  label="Is Director a U.S. person?"
                  rules={[...GENERAL_SCHEME.required]}
                  required
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  shouldUpdate={(prevValues, curValues) => prevValues.us_person !== curValues.us_person}
                  noStyle
                >
                  {({ getFieldValue }) =>
                    getFieldValue('us_person') === true ? (
                      <Form.Item
                        name="director_us_person_tin"
                        label="Provide TIN number"
                        rules={[...GENERAL_SCHEME.required]}
                        required
                      >
                        <Input placeholder="Provide TIN number" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
            <Divider/>
            <Typography.Title level={4}>Beneficial Owner(s) Personal Details</Typography.Title>
            <Form.List name="owners">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => {
                    return (
                      <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                        <Row gutter={[32, 0]}>
                          <Col span={12}>
                            <Form.Item
                              name={[name, "owner_first_name"]}
                              label="Name"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <Input placeholder="First Name" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name={[name, "owner_surname"]}
                              label="Surname"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <Input placeholder="Surname" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name={[name, "owner_date_of_birth"]}
                              label="Date of Birth"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <DatePicker disabledDate={(current) => current && current > new Date()} className="w100" format="YYYY-MM-DD" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name={[name, "owner_nationality"]}
                              label="Nationality"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <Select placeholder="Nationality" options={Object.values(countries).map(country => ({
                                label: country.cn,
                                value: country.cc,
                              }))} optionFilterProp="children"
                                      filterOption={(input, option) => {
                                        return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                      }} showSearch />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name={[name, "owner_ownership_percentage"]}
                              label="Percentage of Ownership"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <InputNumber className="w100" placeholder="Percentage" min={0} max={100} formatter={value => `${value}%`} parser={value => value.replace('%', '')} />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[name, "owner_residence_address"]}
                              label="Residence Address"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <Input.TextArea placeholder="Residence Address" autoSize={{ minRows: 2, maxRows: 6 }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name={[name, "owner_phone_number"]}
                              label="Phone Number"
                              rules={[...GENERAL_SCHEME.required, ...GENERAL_SCHEME.numeric]}
                            >
                              <Input placeholder="Phone Number" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name={[name, "owner_email_address"]}
                              label="Email Address"
                              rules={[...GENERAL_SCHEME.required, ...FIELDS_SCHEME.email]}
                            >
                              <Input placeholder="Email Address" />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[name, "owner_preferred_communication"]}
                              label="Preferred Channel for Communication"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <Radio.Group>
                                <Radio value="email">Email</Radio>
                                <Radio value="phone">Phone</Radio>
                              </Radio.Group>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[name, "owner_politically_exposed"]}
                              label="Is Beneficial owner a politically exposed person (PEP)?"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <Radio.Group>
                                <Radio value={true}>Yes</Radio>
                                <Radio value={false}>No</Radio>
                              </Radio.Group>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[name, "owner_us_person"]}
                              label="Beneficial owner a U.S. person?"
                              rules={[...GENERAL_SCHEME.required]}
                            >
                              <Radio.Group>
                                <Radio value={true}>Yes</Radio>
                                <Radio value={false}>No</Radio>
                              </Radio.Group>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              shouldUpdate={(prevValues, curValues) => prevValues.owner_us_person !== curValues.owner_us_person}
                              noStyle
                            >
                              {({ getFieldValue }) =>
                                getFieldValue('owner_us_person') === true ? (
                                  <Form.Item
                                    name={[name, "owner_tin_number"]}
                                    label="If Yes, provide TIN number:"
                                    rules={[...GENERAL_SCHEME.required]}
                                  >
                                    <Input placeholder="TIN Number" />
                                  </Form.Item>
                                ) : null
                              }
                            </Form.Item>
                          </Col>
                        </Row>

                        <MinusCircleOutlined style={{ fontSize: 32 }} onClick={() => remove(name)} />
                      </Space>
                    )
                  })}
                  {fields.length < 1 && (
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Owner
                    </Button>
                  )}
                </>
              )}
            </Form.List>

            <Button style={{ marginTop: 30 }} size="large" type="primary" htmlType="submit" block>Create corporation</Button>
          </Form>
          <Divider/>
          <Form disabled={!corporationId} className="w100" size="large" variant="filled" form={filesForm} layout="vertical" onFinish={next}>
            <Typography.Title level={4}>Required Documents</Typography.Title>
            {!corporationId && (
              <Alert
                message="Corporation Creation Required"
                description="Please complete the initial corporation registration process before uploading the required documents. This step ensures that your corporation is properly created and recognized in our system"
                type="info"
                style={{ marginBottom: 20 }}
                showIcon
              />
            )}
            <Typography.Title level={5}>Corporate Documents:</Typography.Title>
            <Row gutter={[32, 0]}>
              <Col span={12}>
                <Form.Item
                  name="certificate_of_incorporation"
                  label="Certificate of Incorporation"
                  onChange={(info) => handleFileUploadChange(info, 'Certificate of Incorporation')}
                  rules={[{
                    required: true,
                    validator: async (_, value) => {
                      if (!value || !value.fileList.length) {
                        throw new Error('Please upload the Certificate of Incorporation');
                      }
                    },
                  }]}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="articles_of_association"
                  label="Articles of Association and Memorandum"
                  onChange={(info) => handleFileUploadChange(info, 'Articles of Association and Memorandum')}
                  rules={[{
                    required: true,
                    validator: async (_, value) => {
                      if (!value || !value.fileList.length) {
                        throw new Error('Please upload the Articles of Association and Memorandum');
                      }
                    },
                  }]}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="business_register"
                  label="Extract from Business Register or Good Standing Certificate"
                  onChange={(info) => handleFileUploadChange(info, 'Extract from Business Register or Good Standing Certificate')}
                  rules={[{
                    required: true,
                    validator: async (_, value) => {
                      if (!value || !value.fileList.length) {
                        throw new Error('Please upload the Extract from Business Register or Good Standing Certificate');
                      }
                    },
                  }]}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="shareholder_register"
                  label="Shareholder Register - Detailed list of all shareholders and their respective shareholdings"
                  onChange={(info) => handleFileUploadChange(info, 'Shareholder Register - Detailed list of all shareholders and their respective shareholdings')}
                  rules={[{
                    required: true,
                    validator: async (_, value) => {
                      if (!value || !value.fileList.length) {
                        throw new Error('Please upload the Shareholder Register');
                      }
                    },
                  }]}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="director_register"
                  label="Director Register - Detailed list of all current directors, including their full names, dates of birth, and contact details"
                  onChange={(info) => handleFileUploadChange(info, 'Director Register - Detailed list of all current directors, including their full names, dates of birth, and contact details')}
                  rules={[{
                    required: true,
                    validator: async (_, value) => {
                      if (!value || !value.fileList.length) {
                        throw new Error('Please upload the Director Register');
                      }
                    },
                  }]}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="power_of_attorney"
                  label="Power of Attorney - If applicable, appointing a representative to act on behalf of the company in matters related to the account"
                  onChange={(info) => handleFileUploadChange(info, 'Power of Attorney - If applicable, appointing a representative to act on behalf of the company in matters related to the account')}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="financial_statements"
                  label="Recent Financial Statements - Balance sheet, income statement, and cash flow statement for the past one or two years"
                  onChange={(info) => handleFileUploadChange(info, 'Recent Financial Statements - Balance sheet, income statement, and cash flow statement for the past one or two years')}
                  rules={[{
                    required: true,
                    validator: async (_, value) => {
                      if (!value || !value.fileList.length) {
                        throw new Error('Please upload the Recent Financial Statements');
                      }
                    },
                  }]}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
            <Typography.Title level={5}>Documents for Director/-s:</Typography.Title>
            <Row gutter={[32, 0]}>
              <Col span={12}>
                <Form.Item
                  name="director_identification_document"
                  label="Valid Identification Document"
                  onChange={(info) => handleFileUploadChange(info, 'Valid Identification Document')}
                  rules={[{
                    required: true,
                    validator: async (_, value) => {
                      if (!value || !value.fileList.length) {
                        throw new Error('Please upload the Valid Identification Document');
                      }
                    },
                  }]}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="director_proof_of_address"
                  label="Proof of Address (not older than 6 months)"
                  onChange={(info) => handleFileUploadChange(info, 'Proof of Address')}
                  rules={[{
                    required: true,
                    validator: async (_, value) => {
                      if (!value || !value.fileList.length) {
                        throw new Error('Please upload the Proof of Address');
                      }
                    },
                  }]}
                >
                  <Upload beforeUpload={handleBeforeFileUpload} accept="application/pdf" maxCount={1}>
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>

            <Button style={{ marginTop: 30 }} size="large" type="primary" htmlType="submit" block>Next</Button>
          </Form>
        </Space>
      ),
      icon: <UserOutlined />,
    },
    { title: 'Members',
      content: () => {
        return (
          <div>
            <Space direction="vertical" className="w100">
              <Descriptions layout="vertical" column={1} title="Members">
                {members.map((member, index) => (
                  <Descriptions.Item key={index} label={_.capitalize(member.position)}>
                    {member.email_address ? `${member.first_name} ${member.last_name} - ${member.email_address}` : member.id}&nbsp;
                    (<Typography.Link disabled={loading} onClick={generateSumSubLink.bind(this, member.id)}>Get verification link</Typography.Link>)
                  </Descriptions.Item>
                ))}
              </Descriptions>
              <Button type="dashed" onClick={() => setIsModalVisible(true)} block>
                Add Member
              </Button>
              <Button size="large" type="primary" onClick={next} block>
                Next
              </Button>
            </Space>

            <AddCompanyMemberModal
              visible={isModalVisible}
              onClose={() => setIsModalVisible(false)}
              onSubmit={handleAddMember}
            />

          </div>
        )
      },
      icon: <UsergroupAddOutlined/>,
    },
    {
      title: 'Signing the document',
      content: () => {
        return (
          <Result
            title="Download the contract, sign it, and upload the signed version"
            extra={
              <Space direction="vertical" className="w100" size="large">
                <Button size="large" onClick={handleDownloadTemplate}>Download Template</Button>
                <Form initialValues={{ need_confirm: true }} form={form} layout="vertical" onFinish={sendSignedDocument}>
                  <Form.Item
                    name="signed_document"
                    getValueFromEvent={e => e.fileList}
                    rules={GENERAL_SCHEME.required}
                  >
                    <Upload.Dragger
                      accept="application/pdf"
                      listType="picture"
                      maxCount={1}
                      multiple={false}
                      beforeUpload={handleBeforeFileUpload}
                    >
                      <p className="ant-upload-drag-icon">
                        <FileTextOutlined />
                      </p>
                      <p className="ant-upload-text">Click or drag the signed document to this area</p>
                      <p className="ant-upload-hint">
                        Only one PDF file is allowed. The file must be smaller than 100MB.
                      </p>
                    </Upload.Dragger>
                  </Form.Item>
                  <Form.Item name="need_confirm" valuePropName="checked">
                    <Checkbox style={{ textAlign: 'start' }}>Should this corporation be confirmed? Confirming the corporation will allow it to conduct and receive transfers, as well as access other financial services.</Checkbox>
                  </Form.Item>
                  <Button size="large" type="primary" htmlType="submit" loading={loading}>Continue</Button>
                </Form>
              </Space>
            }
          />
        )
      },
      icon: <FileTextOutlined/>,
    },
    {
      title: 'Done',
      content: () => (
        <Result
          status="success"
          title="Registration Completed"
          subTitle="Your corporation registration is now complete"
          extra={
            <Button size="large" onClick={() => navigate(ROUTES.DASHBOARD.path)}>
              To home page
            </Button>
          }
        />
      ),
      icon: <CheckOutlined/>,
    },
  ];

  return (
    <Container>
      <Spin spinning={loading} tip="Loading...">
        <Block>
          <Typography.Title style={{flexGrow: 1, marginBottom: 40}} level={2}>Corporation Registration</Typography.Title>

          <Steps current={current}>
            {steps.map(item => (
              <Steps.Step key={item.title} title={item.title} icon={item.icon}/>
            ))}
          </Steps>

          <div className={classes.container} style={{marginTop: 40}}>{steps[current].content()}</div>
        </Block>
      </Spin>
    </Container>
  );
};

export default CreateCorporation;
